import React from 'react';
import { Badge } from '@qodo/design-system';
import { usePathSegments } from 'hooks/usePathSegments';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from 'ui/breadcrumb';
import {
  ACCOUNT_IDS,
  AccountId,
  ICONS_MAP,
  ITEM_ID,
  REPOSITORIES_IDS,
  RepositoryId,
} from '../../Sidebar/consts';
import { ROUTE_TO_ITEM_ID_MAP } from '../consts';
import { BreadcrumbsItem } from '../types';
import { getBadgeLabelMapping, getRouteMapping } from '../utils';

export const Breadcrumbs = () => {
  const pathSegments = usePathSegments();
  const routeMapping = getRouteMapping();
  const routeLabels = getBadgeLabelMapping();

  const breadcrumbs: Array<BreadcrumbsItem> = pathSegments
    .map((segment) => {
      const label = routeMapping[segment as keyof typeof routeMapping];
      const badgeLabel = routeLabels[segment as keyof typeof routeLabels];

      if (!label) return undefined;

      return { label, segment, badgeLabel };
    })
    .filter(Boolean) as Array<BreadcrumbsItem>;

  const hasAccountId = breadcrumbs.some((item) =>
    ACCOUNT_IDS.includes(item.segment as AccountId),
  );

  const hasRepositoryId = breadcrumbs.some((item) =>
    REPOSITORIES_IDS.includes(item.segment as RepositoryId),
  );

  const renderIcon = (item: BreadcrumbsItem) => {
    const itemId =
      ROUTE_TO_ITEM_ID_MAP[item.segment as keyof typeof ROUTE_TO_ITEM_ID_MAP];

    if (itemId) {
      return ICONS_MAP[itemId];
    }

    if (hasAccountId) {
      return ICONS_MAP[ITEM_ID.ACCOUNT];
    }

    if (hasRepositoryId) {
      return ICONS_MAP[ITEM_ID.MANAGEMENT_REPOSITORIES];
    }

    return <></>;
  };

  const displayedBreadcrumbs =
    hasAccountId || hasRepositoryId ? breadcrumbs.slice(0, 1) : breadcrumbs;

  const renderItem = (item: BreadcrumbsItem, index: number) => {
    const isLast = displayedBreadcrumbs.length - 1 === index;
    const icon = renderIcon(item);

    return (
      <React.Fragment key={`breadcrumb-item-${index}`}>
        <BreadcrumbItem className="cursor-default">
          <BreadcrumbPage className="flex items-center gap-2">
            {icon && <span className="flex items-center size-4">{icon}</span>}
            {item.label}
          </BreadcrumbPage>
          {item.badgeLabel && <Badge>{item.badgeLabel}</Badge>}
        </BreadcrumbItem>
        {!isLast && <BreadcrumbSeparator />}
      </React.Fragment>
    );
  };

  if (displayedBreadcrumbs.length === 0) {
    return <></>;
  }

  return (
    <Breadcrumb>
      <BreadcrumbList>{displayedBreadcrumbs.map(renderItem)}</BreadcrumbList>
    </Breadcrumb>
  );
};
